import React, { useState, useEffect } from 'react';
import { dispenseAPI, prescriptionAPI, stockAPI } from '../../lib/api';
import { formatDateTime, formatCurrency, getStatusColor, apiErrorMessage } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Pill, ShoppingCart, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../../lib/logger';

const PharmacyDispense = () => {
  const [prescriptions, setPrescriptions] = useState([]);
  const [stock, setStock] = useState([]);
  const [dispenses, setDispenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [dispenseItems, setDispenseItems] = useState([]);
  const [selectedMedicine, setSelectedMedicine] = useState('');
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [presRes, stockRes, dispRes] = await Promise.all([
        prescriptionAPI.getAll({ status: 'active' }),
        stockAPI.getAll({}),
        dispenseAPI.getAll({}),
      ]);
      setPrescriptions(presRes.data.prescriptions || presRes.data || []);
      setStock(stockRes.data.stock || stockRes.data || []);
      setDispenses(dispRes.data.dispenses || dispRes.data || []);
    } catch (error) {
      logger.error('Error loading pharmacy data:', error);
      toast.error(apiErrorMessage(error, 'Failed to load pharmacy data. Please try again.'));
      setPrescriptions([]);
      setStock([]);
      setDispenses([]);
    } finally {
      setLoading(false);
    }
  };

  const openDispenseDialog = (prescription) => {
    setSelectedPrescription(prescription);
    setDispenseItems([]);
    setDialogOpen(true);
  };

  const addItem = () => {
    if (!selectedMedicine || quantity < 1) return;
    
    const medicine = stock.find(s => s.id === selectedMedicine);
    if (!medicine) return;
    
    if (medicine.quantity < quantity) {
      toast.error('Insufficient stock');
      return;
    }
    
    const existing = dispenseItems.find(i => i.medicine_id === selectedMedicine);
    if (existing) {
      toast.error('Medicine already added');
      return;
    }
    
    setDispenseItems([
      ...dispenseItems,
      {
        medicine_id: medicine.id,
        medicine_name: medicine.medicine_name,
        quantity: quantity,
        unit_price: medicine.unit_price,
      },
    ]);
    setSelectedMedicine('');
    setQuantity(1);
  };

  const removeItem = (index) => {
    setDispenseItems(dispenseItems.filter((_, i) => i !== index));
  };

  const totalAmount = dispenseItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

  const handleDispense = async () => {
    if (dispenseItems.length === 0) {
      toast.error('Add at least one medicine');
      return;
    }
    
    try {
      await dispenseAPI.create({
        prescription_id: selectedPrescription.id,
        patient_id: selectedPrescription.patient_id,
        items: dispenseItems,
      });
      toast.success('Medicines dispensed successfully');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to dispense'));
    }
  };

  return (
    <div data-testid="pharmacy-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Pharmacy</h1>
            <p className="text-slate-500">Dispense medicines for prescriptions</p>
          </div>
        </div>
      </header>

      <div className="page-content space-y-6">
        {/* Active Prescriptions */}
        <Card>
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
              <Pill className="w-5 h-5 text-red-500" />
              Active Prescriptions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading...</div>
            ) : prescriptions.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No active prescriptions.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Prescription ID</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Doctor</TableHead>
                    <TableHead>Medicines</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {prescriptions.map((pres) => (
                    <TableRow key={pres.id} data-testid={`prescription-row-${pres.id}`}>
                      <TableCell className="font-mono text-sm">{pres.prescription_id}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{pres.patient?.full_name}</p>
                          <p className="text-xs text-slate-500">{pres.patient?.patient_id}</p>
                        </div>
                      </TableCell>
                      <TableCell>Dr. {pres.doctor?.full_name}</TableCell>
                      <TableCell>{pres.medicines?.length || 0} items</TableCell>
                      <TableCell>{formatDateTime(pres.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDispenseDialog(pres)}
                          data-testid={`dispense-${pres.id}`}
                        >
                          <ShoppingCart className="w-4 h-4 mr-1" />
                          Dispense
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Recent Dispenses */}
        <Card>
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg font-semibold font-[Manrope]">Recent Dispenses</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {dispenses.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No dispenses yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dispense ID</TableHead>
                    <TableHead>Prescription</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dispenses.slice(0, 10).map((dis) => (
                    <TableRow key={dis.id}>
                      <TableCell className="font-mono text-sm">{dis.dispense_id}</TableCell>
                      <TableCell>{dis.prescription_id}</TableCell>
                      <TableCell>{dis.items?.length || 0} items</TableCell>
                      <TableCell className="font-semibold">{formatCurrency(dis.total_amount)}</TableCell>
                      <TableCell>{formatDateTime(dis.dispensed_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dispense Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Dispense Medicines</DialogTitle>
          </DialogHeader>
          {selectedPrescription && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Patient</p>
                  <p className="font-medium">{selectedPrescription.patient?.full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Prescription ID</p>
                  <p className="font-mono">{selectedPrescription.prescription_id}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-slate-500 mb-2">Prescribed Medicines:</p>
                <div className="space-y-1">
                  {selectedPrescription.medicines?.map((med, i) => (
                    <p key={`${med.medicine_name}-${med.dosage}`} className="text-sm">
                      • {med.medicine_name} - {med.dosage} ({med.frequency}, {med.duration})
                    </p>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4">
                <Label className="mb-2 block">Add Medicines to Dispense</Label>
                <div className="flex gap-2">
                  <Select value={selectedMedicine} onValueChange={setSelectedMedicine}>
                    <SelectTrigger className="flex-1" data-testid="select-medicine">
                      <SelectValue placeholder="Select medicine from stock" />
                    </SelectTrigger>
                    <SelectContent>
                      {stock.filter(s => s.quantity > 0).map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.medicine_name} (Stock: {s.quantity}, ₹{s.unit_price})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                    className="w-20"
                    data-testid="input-quantity"
                  />
                  <Button onClick={addItem} data-testid="add-item">Add</Button>
                </div>
              </div>

              {dispenseItems.length > 0 && (
                <div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Medicine</TableHead>
                        <TableHead>Qty</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dispenseItems.map((item, index) => (
                        <TableRow key={item.medicine_id}>
                          <TableCell>{item.medicine_name}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>{formatCurrency(item.unit_price)}</TableCell>
                          <TableCell>{formatCurrency(item.quantity * item.unit_price)}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => removeItem(index)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="flex justify-end mt-4 p-4 bg-slate-50 rounded-lg">
                    <p className="text-lg font-semibold">Total: {formatCurrency(totalAmount)}</p>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button
                  className="bg-red-500 hover:bg-red-600"
                  onClick={handleDispense}
                  disabled={dispenseItems.length === 0}
                  data-testid="confirm-dispense"
                >
                  Confirm Dispense
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PharmacyDispense;
